import React from "react"
import { Chip } from "@mui/material"
import {
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  LocalShipping as ShippingIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Pause as PauseIcon,
} from "@mui/icons-material"

interface StatusBadgeProps {
  status: string
  size?: "small" | "medium"
  showIcon?: boolean
}

const StatusBadge: React.FC<StatusBadgeProps> = ({
  status,
  size = "medium",
  showIcon = true,
}) => {
  const getStatusConfig = (status: string) => {
    const normalizedStatus = status.toLowerCase()

    switch (normalizedStatus) {
      case "delivered":
      case "completed":
      case "success":
        return {
          label: "Delivered",
          color: "#10b981",
          icon: <CheckCircleIcon />,
          background: "linear-gradient(135deg, #10b981 0%, #34d399 100%)",
        }
      case "in transit":
      case "shipped":
      case "active":
        return {
          label: "In Transit",
          color: "#3b82f6",
          icon: <ShippingIcon />,
          background: "linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%)",
        }
      case "pending":
      case "processing":
      case "scheduled":
        return {
          label: "Pending",
          color: "#f59e0b",
          icon: <ScheduleIcon />,
          background: "linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)",
        }
      case "warning":
      case "delayed":
      case "at facility":
        return {
          label: "Warning",
          color: "#f59e0b",
          icon: <WarningIcon />,
          background: "linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)",
        }
      case "error":
      case "failed":
      case "cancelled":
        return {
          label: "Error",
          color: "#ef4444",
          icon: <ErrorIcon />,
          background: "linear-gradient(135deg, #ef4444 0%, #f87171 100%)",
        }
      case "paused":
      case "on hold":
        return {
          label: "Paused",
          color: "#6b7280",
          icon: <PauseIcon />,
          background: "linear-gradient(135deg, #6b7280 0%, #9ca3af 100%)",
        }
      default:
        return {
          label: status,
          color: "#6b7280",
          icon: <ScheduleIcon />,
          background: "linear-gradient(135deg, #6b7280 0%, #9ca3af 100%)",
        }
    }
  }

  const config = getStatusConfig(status)

  return (
    <Chip
      label={config.label}
      icon={showIcon ? config.icon : undefined}
      size={size}
      sx={{
        background: config.background,
        color: "white",
        fontWeight: 600,
        fontSize: size === "small" ? "0.75rem" : "0.875rem",
        height: size === "small" ? 24 : 32,
        "& .MuiChip-icon": {
          color: "white",
          fontSize: size === "small" ? "1rem" : "1.2rem",
        },
        "& .MuiChip-label": {
          px: 1,
        },
        boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
        transition: "all 0.2s ease-in-out",
        "&:hover": {
          transform: "translateY(-1px)",
          boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.15)",
        },
      }}
    />
  )
}

export default StatusBadge
